import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { ProseMirrorContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';
import { convertProsemirrorToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { addAnalytics } from '../analytics/utils';
import { ErrorUtils } from '../utils/errors';
import { getContextSelectionScope } from './utils';
/**
 * Takes a prosemirror schema and (slice or node) and returns it as
 * markdown.
 * Currently supports the following modes;
 * - plain text (limited nodes are converted to plain text representations e.g. emoji, mentions, date)
 * - markdown (https://product-fabric.atlassian.net/wiki/spaces/EUXQ/pages/3632267938/Mapping+node+and+mark+names+to+markdown+features)
 *
 * @example
 * ```ts
 * const markdown = sliceOrNodeToMarkdown({
 *   slice: slice,
 *   editorView: editorView,
 *   intl: intl,
 * });
 * ```
 */
export function sliceOrNodeToMarkdown({
  slice,
  node,
  editorView,
  intl,
  convertTo,
  updateIdMap,
  selectionType
}) {
  let fragment = (slice || node).content;
  try {
    // we track whether the context is a whole document or a partial selection
    const contextSelectionScope = getContextSelectionScope({
      editorView,
      slice,
      node
    });
    if (getBooleanFF('platform.editor.ai-markdown-plus_9v1w7') && convertTo === 'markdown-plus') {
      const {
        markdown,
        idMap,
        fallbackState,
        contentStatistics
      } = convertProsemirrorToMarkdown({
        editorView,
        fragment,
        featureToggles: {
          markdownPlus: true,
          // This ternary is needed because feature flags can only be called as part of
          // an expression or we get a lint error & ignoring the error prevents the
          // cleanup script from finding the flag.
          markdownPlusV2: getBooleanFF('platform.editor.ai-markdown-plus-v2_j5m0c') ? true : false,
          markdownPlusMedia: getBooleanFF('platform.editor.ai-markdown-plus-media_svrli') ? true : false,
          markdownPlusExtensions: getBooleanFF('platform.editor.ai-markdown-plus-extension_m8eb1') ? true : false,
          markdownPlusExpands: getBooleanFF('platform.editor.ai-markdown-plus-expands_5h7qa') ? true : false,
          markdownPlusPanels: getBooleanFF('platform.editor.ai-markdown-plus-panels_veh92') ? true : false,
          markdownPlusDecisions: getBooleanFF('platform.editor.ai-markdown-plus-decisions_4lriq') ? true : false,
          markdownPlusTables: getBooleanFF('platform.editor.ai-markdown-plus-tables_ipb1h') ? true : false,
          markdownPlusLayouts: getBooleanFF('platform.editor.ai-markdown-plus-layouts_jw2zj') ? true : false,
          markdownPlusNestedExpands: getBooleanFF('platform.editor.ai-markdown-plus-nested-expands_ru5xx') ? true : false,
          markdownPlusMarks: getBooleanFF('platform.editor.ai-markdown-plus-marks_eitjo') ? true : false
        }
      });
      if (!!Object.keys(fallbackState).length) {
        const tr = addAnalytics({
          editorState: editorView.state,
          tr: editorView.state.tr,
          payload: {
            action: 'fallback',
            actionSubject: 'editorPluginAI',
            actionSubjectId: 'markdownSerializer',
            attributes: {
              fallbackState
            },
            eventType: 'operational'
          }
        });
        editorView.dispatch(tr);
      }

      // This ID map is used to map the markdown back to the original ProseMirror
      // when we receive transformed content back from the llm.
      updateIdMap({
        idMap,
        selectionType
      });
      return {
        markdown,
        contentStatistics: {
          ...contentStatistics,
          contextSelectionScope
        }
      };
    }
    if (convertTo === 'markdown' || convertTo === 'markdown-plus') {
      const {
        markdown,
        contentStatistics
      } = convertProsemirrorToMarkdown({
        editorView,
        fragment,
        featureToggles: {}
      });
      return {
        markdown,
        contentStatistics: {
          ...contentStatistics,
          contextSelectionScope
        }
      };
    }
    const markdown = getTextFromNode(fragment, intl);

    // Wrap the fragment in a temporary doc node so we can use ProseMirrorContentStatistics
    const tempNode = editorView.state.schema.nodes.doc.createAndFill(null, fragment);
    let contentStatistics;
    if (tempNode) {
      const pmStats = new ProseMirrorContentStatistics(tempNode);
      contentStatistics = pmStats.collectStatistics();
    }
    return {
      markdown,
      contentStatistics: contentStatistics && {
        ...contentStatistics,
        contextSelectionScope
      }
    };
  } catch (e) {
    const tr = addAnalytics({
      editorState: editorView.state,
      tr: editorView.state.tr,
      payload: {
        action: 'failed',
        actionSubject: 'editorPluginAI',
        actionSubjectId: 'markdownSerializer',
        attributes: {
          errorMessage: ErrorUtils.extractErrorMessage(e)
        },
        eventType: 'operational'
      }
    });
    editorView.dispatch(tr);
    return {
      markdown: '',
      contentStatistics: undefined
    };
  }
}

// Note -- plain text use without markdown support is expected to be
// deprecated following feature roll out.
const getTextFromNode = (fragment, intl) => {
  let markdown = '';

  // Iterate through the content of the fragment
  fragment.forEach(extractTextFromNode);
  return markdown;

  // Helper function to recursively extract text from nodes and their children
  function extractTextFromNode(node) {
    if (node.isText) {
      const linkMarkIndex = node.marks.findIndex(x => x.type.name === 'link');
      if (linkMarkIndex !== -1) {
        var _node$marks$linkMarkI, _node$marks$linkMarkI2;
        const href = (_node$marks$linkMarkI = node.marks[linkMarkIndex]) === null || _node$marks$linkMarkI === void 0 ? void 0 : (_node$marks$linkMarkI2 = _node$marks$linkMarkI.attrs) === null || _node$marks$linkMarkI2 === void 0 ? void 0 : _node$marks$linkMarkI2.href;
        markdown += `${href} `;
      } else {
        markdown += `${node.text} `;
      }
    } else {
      switch (node.type.name) {
        case 'inlineCard':
        case 'blockCard':
        case 'embedCard':
          markdown += `${node.attrs.url} `;
          break;
        case 'emoji':
          markdown += `${node.attrs.text || node.attrs.shortName} `;
          break;
        case 'date':
          markdown += `${intl.formatDate(Number(node.attrs.timestamp))} `;
          break;
        case 'status':
        case 'mention':
          markdown += `${node.attrs.text} `;
          break;
        default:
          node.forEach(extractTextFromNode);
      }
    }
  }
};